import { useState, useEffect } from 'react'
import { Trash2, FileText, Sparkles, ArrowLeft, Wand2, FolderTree, ChevronDown, ChevronUp, Download, Loader2 } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from '@/components/ui/select'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { useDRDs, useDRD, useDeleteDRD, useUpdateDRD } from '@/features/requirements/api/useRequirements'
import { useSubmitDRDTestGeneration } from '@/features/test-cases/api/useAIGeneration'
import { useDocument, useDownloadArtifact, generateRequirementsDocument } from '@/features/requirements/api/useDocuments'
import { MicrosuiteDialog } from './MicrosuiteDialog'
import { getDRDStatusColor } from '@/types/requirement.types'
import type { DRD } from '@/types/requirement.types'
import { formatDate } from '@/lib/utils'
import { logger } from '@/lib/utils/logger'

interface DRDListProps {
    projectId?: number
    drdId?: number
}

export function DRDList({ projectId, drdId }: DRDListProps) {
    const navigate = useNavigate()
    const { id: routeProjectId } = useParams()

    const [deleteTarget, setDeleteTarget] = useState<DRD | null>(null)
    const [microsuiteDialogOpen, setMicrosuiteDialogOpen] = useState(false)
    const [expandedReqId, setExpandedReqId] = useState<number | null>(null)
    const [generatedDocumentId, setGeneratedDocumentId] = useState<number | null>(null)

    const { data: singleDRD, isLoading: isLoadingSingle, error: errorSingle } = useDRD(drdId || 0)
    const { data: drds, isLoading, error } = useDRDs(
        projectId ? { project_id: projectId } : undefined
    )
    const updateMutation = useUpdateDRD()
    const deleteMutation = useDeleteDRD()
    const generateTestsMutation = useSubmitDRDTestGeneration()
    const { data: generatedDoc } = useDocument(generatedDocumentId)
    const downloadArtifact = useDownloadArtifact()

    const showingSingleDRD = !!drdId && !!singleDRD
    const activeLoading = drdId ? isLoadingSingle : isLoading
    const activeError = drdId ? errorSingle : error

    const getResolvedProjectId = (): number =>
        singleDRD?.test_project_id || (routeProjectId ? parseInt(routeProjectId, 10) : 0)

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return
        try {
            await deleteMutation.mutateAsync(deleteTarget.id)
            toast.success(`DRD "${deleteTarget.title}" deleted successfully`)
            setDeleteTarget(null)
        } catch (err) {
            logger.error('Delete DRD failed', err)
            toast.error('Failed to delete DRD. Please try again.')
        }
    }

    const handleStatusChange = async (id: number, newStatus: string) => {
        try {
            await updateMutation.mutateAsync({ id, data: { status: newStatus } })
            toast.success(`Status updated to ${newStatus}`)
        } catch (err) {
            logger.error('Status update failed', err)
            toast.error('Failed to update status. Please try again.')
        }
    }

    const handleGenerateDocument = async () => {
        if (!singleDRD) return

        try {
            toast.info('Generating IEEE 830-1998 compliant requirements document...')
            const doc = await generateRequirementsDocument(singleDRD, 'pdf')
            setGeneratedDocumentId(doc.id)
        } catch (err) {
            logger.error('Failed to generate requirements document', err)
            toast.error('Failed to generate document. Please try again.')
        }
    }

    // Auto-download when document generation completes.
    // Backend artifact rows expose `format`; older frontend code used `output_format`.
    // Support both so completed documents created by the result handler are visible.
    useEffect(() => {
        if (generatedDoc?.status === 'completed') {
            const artifacts = generatedDoc.artifacts || []
            const pdfArtifact = artifacts.find((artifact) => {
                const format = String(artifact.format ?? artifact.output_format ?? '').toLowerCase()
                return format === 'pdf' || artifact.content_type === 'application/pdf'
            })

            if (pdfArtifact) {
                toast.success('Requirements document generated successfully!')
                downloadArtifact.mutate({
                    documentId: generatedDoc.id,
                    artifactId: pdfArtifact.id,
                })
                setGeneratedDocumentId(null)
            } else if (artifacts.length === 0) {
                logger.warn('Document completed but no artifacts were returned yet', {
                    documentId: generatedDoc.id,
                })
            } else {
                logger.warn('Document completed but no PDF artifact was found', {
                    documentId: generatedDoc.id,
                    artifacts,
                })
            }
        } else if (generatedDoc?.status === 'failed') {
            toast.error(`Document generation failed: ${generatedDoc.error_message || 'Unknown error'}`)
            setGeneratedDocumentId(null)
        }
    }, [generatedDoc, downloadArtifact])

    const detectLanguageFromDRD = (drd: DRD): string => {
        if (!drd.requirements || drd.requirements.length === 0) return 'unknown'
        const extensions: Record<string, number> = {}
        drd.requirements.forEach((req) => {
            if (req.source_file) {
                const ext = req.source_file.split('.').pop()?.toLowerCase()
                if (ext) extensions[ext] = (extensions[ext] || 0) + 1
            }
        })
        const langMap: Record<string, string> = {
            rs: 'rust', py: 'python', go: 'go', ts: 'typescript', tsx: 'typescript',
            js: 'javascript', jsx: 'javascript', java: 'java', cs: 'csharp',
            cpp: 'cpp', c: 'c', rb: 'ruby', php: 'php', swift: 'swift', kt: 'kotlin',
        }
        let maxExt = ''
        let maxCount = 0
        for (const [ext, count] of Object.entries(extensions)) {
            if (count > maxCount) { maxCount = count; maxExt = ext }
        }
        return langMap[maxExt] || 'unknown'
    }

    const handleGenerateTestCases = async () => {
        if (!singleDRD) return
        const resolvedProjectId = getResolvedProjectId()
        if (!resolvedProjectId) {
            toast.error('Cannot determine project ID for test generation.')
            return
        }
        try {
            await generateTestsMutation.mutateAsync({
                project_id: resolvedProjectId,
                drd_id: singleDRD.id,
                generation_type: detectLanguageFromDRD(singleDRD),
                max_test_cases: singleDRD.requirements?.length
                    ? singleDRD.requirements.length * 3
                    : 50,
            })
            toast.success('Test case generation started. You will be notified when complete.')
        } catch (err) {
            logger.error('Test generation failed', err)
            toast.error('Failed to start test case generation. Please try again.')
        }
    }

    if (activeLoading) {
        return (
            <Card>
                <CardContent className="py-12">
                    <div className="flex items-center justify-center text-gray-500">
                        {drdId ? 'Loading DRD Requirements...' : 'Loading Design Requirements Documents...'}
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (activeError) {
        return (
            <Card>
                <CardContent className="py-12">
                    <div className="flex flex-col items-center justify-center text-red-500">
                        <p>Failed to load {drdId ? 'DRD' : 'DRDs'}</p>
                        <p className="text-sm text-gray-500 mt-2">
                            {activeError instanceof Error ? activeError.message : 'Unknown error'}
                        </p>
                    </div>
                </CardContent>
            </Card>
        )
    }

    // ── Single DRD detail view ──────────────────────────────────────────
    if (showingSingleDRD) {
        const qualityMetrics = singleDRD.content?.quality_metrics
        const hasSourceFiles = singleDRD.requirements?.some((r) => r.source_file)
        const resolvedProjectId = getResolvedProjectId()

        return (
            <div className="space-y-4">
                {/* Navigation and actions */}
                <div className="flex items-center gap-2 mb-4">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/requirements-ops/requirements')}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Requirements
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleGenerateDocument}
                        disabled={!!generatedDocumentId}
                        className="print:hidden"
                    >
                        {generatedDocumentId ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {generatedDoc?.status === 'processing' && 'Processing...'}
                                {generatedDoc?.status === 'rendering' && 'Rendering PDF...'}
                                {generatedDoc?.status === 'uploading' && 'Uploading...'}
                                {(!generatedDoc || generatedDoc.status === 'queued') && 'Generating...'}
                            </>
                        ) : (
                            <>
                                <Download className="h-4 w-4 mr-2" />
                                Generate IEEE 830 PDF
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setMicrosuiteDialogOpen(true)}
                        disabled={!hasSourceFiles || !resolvedProjectId}
                        className="print:hidden"
                    >
                        <FolderTree className="h-4 w-4 mr-2" />
                        Create Microsuites
                    </Button>
                    <Button
                        variant="default"
                        onClick={handleGenerateTestCases}
                        disabled={
                            generateTestsMutation.isPending ||
                            !singleDRD.requirements ||
                            singleDRD.requirements.length === 0
                        }
                        className="print:hidden"
                    >
                        <Wand2 className="h-4 w-4 mr-2" />
                        {generateTestsMutation.isPending ? 'Starting...' : 'Generate Test Cases'}
                    </Button>
                </div>

                {/* DRD Header */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>{singleDRD.title}</CardTitle>
                                <CardDescription>
                                    Version {singleDRD.version} · Created {formatDate(singleDRD.created_at)}
                                    {singleDRD.updated_at && singleDRD.updated_at !== singleDRD.created_at && (
                                        <> · Updated {formatDate(singleDRD.updated_at)}</>
                                    )}
                                    {singleDRD.created_by_name && ` by ${singleDRD.created_by_name}`}
                                </CardDescription>
                            </div>
                            <Badge variant={getDRDStatusColor(singleDRD.status)}>
                                {singleDRD.status.charAt(0).toUpperCase() + singleDRD.status.slice(1)}
                            </Badge>
                        </div>
                    </CardHeader>
                </Card>

                {/* Quality Metrics */}
                {qualityMetrics && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Code Metrics</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>Functions:</span>
                                        <span className="font-semibold">{qualityMetrics.total_functions}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Classes:</span>
                                        <span className="font-semibold">{qualityMetrics.total_classes}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Lines of Code:</span>
                                        <span className="font-semibold">
                                            {qualityMetrics.total_lines_of_code?.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Quality Indicators</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 text-sm">
                                    <div>
                                        <div className="flex justify-between mb-1">
                                            <span>Complexity:</span>
                                            <span className="font-semibold">
                                                {qualityMetrics.average_cyclomatic_complexity?.toFixed(1)}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${
                                                    (qualityMetrics.average_cyclomatic_complexity || 0) <= 10
                                                        ? 'bg-green-500'
                                                        : (qualityMetrics.average_cyclomatic_complexity || 0) <= 20
                                                            ? 'bg-yellow-500'
                                                            : 'bg-red-500'
                                                }`}
                                                style={{
                                                    width: `${Math.min(
                                                        (qualityMetrics.average_cyclomatic_complexity || 0) * 5,
                                                        100
                                                    )}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex justify-between mb-1">
                                            <span>Duplication:</span>
                                            <span className="font-semibold">
                                                {((qualityMetrics.code_duplication_estimate || 0) * 100).toFixed(0)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${
                                                    (qualityMetrics.code_duplication_estimate || 0) <= 0.05
                                                        ? 'bg-green-500'
                                                        : (qualityMetrics.code_duplication_estimate || 0) <= 0.1
                                                            ? 'bg-yellow-500'
                                                            : 'bg-red-500'
                                                }`}
                                                style={{
                                                    width: `${Math.min(
                                                        (qualityMetrics.code_duplication_estimate || 0) * 100,
                                                        100
                                                    )}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Requirements Summary</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm">Total Requirements:</span>
                                        <span className="text-lg font-bold">{singleDRD.requirements?.length || 0}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm">Functional:</span>
                                        <span className="text-lg font-bold">
                                            {singleDRD.requirements?.filter((r) => r.requirement_type === 'functional').length || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm">Non-Functional:</span>
                                        <span className="text-lg font-bold">
                                            {singleDRD.requirements?.filter((r) => r.requirement_type === 'non-functional').length || 0}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Requirements Table — grouped by suite */}
                <Card>
                    <CardHeader>
                        <CardTitle>Requirements ({singleDRD.requirements?.length || 0})</CardTitle>
                        <CardDescription>AI-generated requirements extracted from your source code, grouped by architectural suite. Click a row to expand its full description.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {singleDRD.requirements && singleDRD.requirements.length > 0 ? (() => {
                            // Group by category (suite name)
                            const grouped = singleDRD.requirements!.reduce<Record<string, typeof singleDRD.requirements>>((acc, req) => {
                                const suite = (req as any).category || 'general'
                                if (!acc[suite]) acc[suite] = []
                                acc[suite]!.push(req)
                                return acc
                            }, {})
                            const suites = Object.keys(grouped).sort()

                            return (
                                <div className="space-y-6">
                                    {suites.map((suite) => (
                                        <div key={suite}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <Badge variant="secondary" className="font-mono text-xs">
                                                    {suite}
                                                </Badge>
                                                <span className="text-xs text-muted-foreground">
                                                    {grouped[suite]!.length} requirement{grouped[suite]!.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            <div className="rounded-md border">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead className="w-6" />
                                                            <TableHead>ID</TableHead>
                                                            <TableHead>Title</TableHead>
                                                            <TableHead>Type</TableHead>
                                                            <TableHead>Priority</TableHead>
                                                            <TableHead>Complexity</TableHead>
                                                            <TableHead>Source</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {grouped[suite]!.map((req) => {
                                                            const isExpanded = expandedReqId === req.id
                                                            return (
                                                                <>
                                                                    <TableRow
                                                                        key={req.id}
                                                                        className="cursor-pointer hover:bg-muted/50"
                                                                        onClick={() => setExpandedReqId(isExpanded ? null : req.id)}
                                                                    >
                                                                        <TableCell className="pr-0">
                                                                            {isExpanded
                                                                                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                                                                : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <span className="font-mono text-sm">{req.requirement_id}</span>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <div className="font-medium">{(req as any).title || '—'}</div>
                                                                            {!isExpanded && (
                                                                                <div className="text-xs text-muted-foreground line-clamp-2">
                                                                                    {req.description}
                                                                                </div>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Badge variant="outline">{req.requirement_type || '—'}</Badge>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {req.priority ? (
                                                                                <Badge
                                                                                    variant={
                                                                                        req.priority === 'high'
                                                                                            ? 'destructive'
                                                                                            : req.priority === 'medium'
                                                                                                ? 'default'
                                                                                                : 'secondary'
                                                                                    }
                                                                                >
                                                                                    {req.priority}
                                                                                </Badge>
                                                                            ) : '—'}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <span className="text-sm">{(req as any).complexity || '—'}</span>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <span className="text-xs text-muted-foreground font-mono">
                                                                                {req.source_file || '—'}
                                                                            </span>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                    {isExpanded && (
                                                                        <TableRow key={`${req.id}-expanded`} className="bg-muted/30 hover:bg-muted/30">
                                                                            <TableCell />
                                                                            <TableCell colSpan={6} className="py-3">
                                                                                <p className="text-sm text-foreground whitespace-pre-wrap">
                                                                                    {req.description || 'No description available.'}
                                                                                </p>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )}
                                                                </>
                                                            )
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        })() : (
                            <div className="text-center py-12">
                                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-4 text-lg font-semibold">No Requirements Found</h3>
                                <p className="text-sm text-gray-500 mt-2">
                                    This DRD does not contain any requirements yet.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Microsuite creation dialog */}
                <MicrosuiteDialog
                    open={microsuiteDialogOpen}
                    onOpenChange={setMicrosuiteDialogOpen}
                    drd={singleDRD}
                    projectId={resolvedProjectId}
                />
            </div>
        )
    }

    // ── DRD list view ───────────────────────────────────────────────────
    if (!drds || drds.length === 0) {
        return (
            <Card>
                <CardContent className="py-12">
                    <div className="flex flex-col items-center justify-center">
                        <Sparkles className="h-12 w-12 text-gray-400" />
                        <h3 className="mt-4 text-lg font-semibold">No AI-Augmented DRDs Yet</h3>
                        <p className="text-sm text-gray-500 mt-2">
                            Upload source code to automatically generate Design Requirements Documents
                        </p>
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-purple-500" />
                                AI-Augmented Requirements Documents
                            </CardTitle>
                            <CardDescription>
                                AI-Augmented Design Requirements Documents automatically generated from your source code
                            </CardDescription>
                        </div>
                        <Badge variant="outline" className="text-lg font-semibold">
                            {drds.length} {drds.length === 1 ? 'Document' : 'Documents'}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Version</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Analyzer</TableHead>
                                    <TableHead>Confidence</TableHead>
                                    <TableHead>Requirements</TableHead>
                                    <TableHead>Last Updated</TableHead>
                                    <TableHead className="w-[100px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {drds.map((drd) => (
                                    <TableRow
                                        key={drd.id}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() =>
                                            navigate(`/requirements-ops/requirements?drd_id=${drd.id}`)
                                        }
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <FileText className="h-4 w-4 text-purple-500" />
                                                <div>
                                                    <div className="font-medium">
                                                        {drd.project_name || 'Untitled Project'}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">{drd.title}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="font-mono text-sm">{drd.version}</span>
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                value={drd.status}
                                                onValueChange={(val) => handleStatusChange(drd.id, val)}
                                            >
                                                <SelectTrigger
                                                    className="w-[130px]"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <Badge variant={getDRDStatusColor(drd.status)}>
                                                        {drd.status.charAt(0).toUpperCase() + drd.status.slice(1)}
                                                    </Badge>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="draft">Draft</SelectItem>
                                                    <SelectItem value="approved">Approved</SelectItem>
                                                    <SelectItem value="deprecated">Deprecated</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">{drd.analyzer_used || '—'}</div>
                                        </TableCell>
                                        <TableCell>
                                            {drd.confidence_score !== undefined ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${
                                                                drd.confidence_score >= 0.8
                                                                    ? 'bg-green-500'
                                                                    : drd.confidence_score >= 0.6
                                                                        ? 'bg-yellow-500'
                                                                        : 'bg-red-500'
                                                            }`}
                                                            style={{
                                                                width: `${(drd.confidence_score * 100).toFixed(0)}%`,
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="text-xs text-muted-foreground">
                                                        {(drd.confidence_score * 100).toFixed(0)}%
                                                    </span>
                                                </div>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{drd.requirement_count ?? 0}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-sm text-muted-foreground">
                                                {formatDate(drd.updated_at ?? drd.created_at)}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setDeleteTarget(drd)
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            <ConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
                title="Delete DRD"
                description={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
                confirmLabel="Delete"
                variant="destructive"
                loading={deleteMutation.isPending}
                onConfirm={handleConfirmDelete}
            />
        </>
    )
}
